import { ButtonType, ButtonWidget, TextLinkWidget } from '@tableau/widgets';
import React, { useEffect, useState } from 'react';

import { SessionServerSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { ServerUrls } from '~src/commonInfra/url/ServerUrls';
import { ListResult } from '~src/httpClient/fetchers/ListResult';
import { contentCountsUpToDate } from '~src/spacesPlatform/actions/ContentCountsActions';
import { spaceContentsRequestSucceeded } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';
import { ConnectedExplorer } from '~src/spacesPlatform/components/Explorer/ConnectedExplorer';
import { ConnectedSpaceInfoProvider } from '~src/spacesPlatform/components/Explorer/helpers/SpaceInfoProvider';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { SpaceInfo } from '~src/spacesPlatform/state/SpaceInfo';

import { IAdminProfileUser } from '../../models/IAdminProfile';
import { showAddUsersToAdminProfileDialog } from '../../services/AddUsersToAdminProfileDialogAction';
import { AdminProfileStyles } from '../../styles/AdminProfileStyles';

export type AdminProfileServerUser = IAdminProfileUser & { siteCount: number, luids: string[], siteNames: string[] };

/**
 * Deduplicates admin profile users by systemId. When a user is assigned to the profile
 * on multiple sites, the API returns one row per user-site. This groups by systemId,
 * keeps the first occurrence for display, and sets siteCount to the number of sites.
 */
export function getAdminProfileServerUsers(
  users: ReadonlyArray<IAdminProfileUser>
): AdminProfileServerUser[] {
  const usersBySystemId = new Map<number, IAdminProfileUser[]>();
  for (const user of users) {
    const systemId = user.systemUserId!;
    const existing = usersBySystemId.get(systemId) ?? [];
    existing.push(user);
    usersBySystemId.set(systemId, existing);
  }

  return Array.from(usersBySystemId.values()).map((usersWithSameSystemId) => {
    const firstUser = usersWithSameSystemId[0];
    const luids = usersWithSameSystemId.map((u) => u.luid);
    const siteNames = usersWithSameSystemId
      .map((u) => u.siteName)
      .filter((name): name is string => !!name);
    return {
      ...firstUser,
      siteCount: usersWithSameSystemId.length,
      luids,
      siteNames,
    };
  });
}

export interface AdminProfileUsersTabProviderProps {
  users: ReadonlyArray<AdminProfileServerUser>;
  profileId: string;
  profileName: string;
  onProfileUsersUpdated?: () => void;
}

function convertUserToContentInfo(user: AdminProfileServerUser): IContentInfo<'serveruser'> {
  return {
    id: user.id,
    contentType: 'serveruser',
    systemUserId: user.systemUserId,
    name: user.name,
    displayName: user.name,
    username: user.username,
    domainName: undefined,
    luid: user.luid,
    favorite: false,
    updatedAt: new Date(),
    owner: undefined,
    modifiedBy: undefined,
    contact: undefined,
    maxSiteRole: user.role,
    siteCount: user.siteCount,
    luids: user.luids,
    siteNames: user.siteNames,
    readOnly: false,
  } as IContentInfo<'serveruser'>;
}

export const AdminProfileUsersTabProvider: React.FC<AdminProfileUsersTabProviderProps> = ({
  users,
  profileId,
  profileName,
  onProfileUsersUpdated,
}) => {
  const [doesTheServerHaveMoreThanOneSite] = useState(
    () => SessionServerSelectors.doesTheServerHaveMoreThanOneSite(getAppStore().getState())
  );

  const spaceInfo: SpaceInfo = {
    item: { id: profileId, name: profileName, contentType: 'serveradminprofiles' as any },
    spaceType: 'serveradminprofiles',
    subspaceName: 'users',
  };

  useEffect(() => {
    const store = getAppStore();
    const contentItems = users.map(convertUserToContentInfo);
    const listResult = new ListResult(contentItems, 0, contentItems.length);
    store.dispatch(spaceContentsRequestSucceeded(listResult));
    store.dispatch(contentCountsUpToDate());
  }, [users]);

  const introText = (
    <span
      className={AdminProfileStyles.SitesIntroText}
      data-tb-test-id="admin-profile-users-intro-text"
    >
      {Messages.adminProfileUsersIntroText({ PROFILE_NAME: profileName })}
      {doesTheServerHaveMoreThanOneSite && (
        <>
          {' '}
          <TextLinkWidget
            href={ServerUrls.getInstance().usersUrl()}
            testId="admin-profile-users-assign-link"
          >
            {Messages.adminProfileUsersAssignUsersLink()}
          </TextLinkWidget>
        </>
      )}
    </span>
  );

  const assignButton = !doesTheServerHaveMoreThanOneSite ? (
    <ButtonWidget
      buttonType={ButtonType.Outline}
      onClick={() => showAddUsersToAdminProfileDialog(profileId, profileName, users, onProfileUsersUpdated)}
      testId="admin-profile-users-assign-button"
    >
      {Messages.adminProfileUsersAssignButton()}
    </ButtonWidget>
  ) : undefined;

  return (
    <ConnectedSpaceInfoProvider spaceInfo={spaceInfo}>
      <ConnectedExplorer
        supportedDisplayModes={['list']}
        toolbarProps={{
          supportFilterPanel: false,
          hideSelection: true,
          toolbarVisibility: 'enabled',
          toolbarContentSpecificControls: {
            beginning: introText,
            end: assignButton,
          },
        }}
        emptyStateProps={{
          primaryMessage: Messages.adminProfileUsersEmptyTitle(),
          secondaryMessage: Messages.adminProfileUsersEmptyDescription(),
        }}
      />
    </ConnectedSpaceInfoProvider>
  );
};

AdminProfileUsersTabProvider.displayName = 'AdminProfileUsersTabProvider';
